import { z } from 'zod';
import { BaseStatFilterSchema } from '../schemas';

type BaseStatFilter = z.infer<typeof BaseStatFilterSchema>;

/** Standard filter keys that all entities support */
const STANDARD_FILTER_KEYS = ['query', 'years', 'maKhoa', 'maNganh'] as const;

/**
 * Build URLSearchParams from pagination, sorting, and filters.
 * Replaces the duplicated URLSearchParams construction in every entity query hook.
 * @param opts.extraParams - Entity-specific params beyond the standard filter set
 *   (e.g. { maTinhMoi: ['01','02'], top149: true, yearsAchieveTop149: 3 })
 */
export function buildStatsSearchParams(opts: {
	page?: number;
	pageSize?: number;
	sortBy?: string;
	sortOrder?: string;
	filters?: BaseStatFilter;
	filterKeys?: readonly string[];
	extraParams?: Record<string, unknown>;
}): URLSearchParams {
	const params = new URLSearchParams();
	const filterKeys = opts.filterKeys ?? STANDARD_FILTER_KEYS;

	if (opts.page !== undefined) {
		params.append('page', ((opts.page ?? 0) + 1).toString());
	}
	if (opts.pageSize !== undefined) {
		params.append('limit', (opts.pageSize ?? 10).toString());
	}
	if (opts.sortBy) {
		params.append('sortBy', opts.sortBy);
		params.append('sortOrder', opts.sortOrder || 'asc');
	}

	appendFilters(params, opts.filters, filterKeys);
	appendExtraParams(params, opts.extraParams);
	return params;
}

/**
 * Params cho nút xuất Excel: đúng bộ lọc + sắp xếp của lưới, bỏ phân trang để
 * file chứa mọi dòng khớp bộ lọc chứ không chỉ trang đang xem.
 */
export function buildStatsExportParams(opts: {
	sortBy?: string;
	sortOrder?: string;
	filters?: BaseStatFilter;
	filterKeys?: readonly string[];
	extraParams?: Record<string, unknown>;
}): URLSearchParams {
	return buildStatsSearchParams({
		sortBy: opts.sortBy,
		sortOrder: opts.sortOrder,
		filters: opts.filters,
		filterKeys: opts.filterKeys,
		extraParams: opts.extraParams,
	});
}

/**
 * Build URLSearchParams from filters only (no pagination/sorting).
 * Used for chart queries.
 * @param extraParams - Entity-specific params beyond the standard filter set
 */
export function buildChartSearchParams(
	filters?: BaseStatFilter,
	filterKeys?: readonly string[],
	extraParams?: Record<string, unknown>
): URLSearchParams {
	const params = new URLSearchParams();
	appendFilters(params, filters, filterKeys ?? STANDARD_FILTER_KEYS);
	appendExtraParams(params, extraParams);
	return params;
}

/**
 * Build a flattened query key array from pagination, sorting, and filters.
 * Uses primitive values for stable TanStack Query cache keys.
 */
export function buildStatsQueryKey(
	entityKey: string,
	opts: {
		page?: number;
		pageSize?: number;
		sortBy?: string;
		sortOrder?: string;
		filters?: BaseStatFilter;
		filterKeys?: readonly string[];
		extraParams?: Record<string, unknown>;
	}
): unknown[] {
	const filterKeys = opts.filterKeys ?? STANDARD_FILTER_KEYS;
	const key: unknown[] = [
		entityKey,
		'stats',
		opts.page,
		opts.pageSize,
		opts.sortBy,
		opts.sortOrder,
	];

	for (const fk of filterKeys) {
		if (fk === 'query') {
			key.push(opts.filters?.query);
		} else {
			const arr = opts.filters?.[fk as keyof BaseStatFilter] as unknown[] | undefined;
			key.push(arr?.slice().sort().join(','));
		}
	}

	// Flatten extra params into query key for stable caching
	if (opts.extraParams) {
		for (const [k, v] of Object.entries(opts.extraParams).sort(([a], [b]) => a.localeCompare(b))) {
			if (Array.isArray(v)) {
				key.push(`${k}:${v.slice().sort().join(',')}`);
			} else if (v !== undefined && v !== null) {
				key.push(`${k}:${v}`);
			}
		}
	}

	return key;
}

function appendFilters(
	params: URLSearchParams,
	filters: BaseStatFilter | undefined,
	filterKeys: readonly string[]
): void {
	if (!filters) return;

	for (const key of filterKeys) {
		if (key === 'query') {
			if (filters.query) params.append('query', filters.query);
			continue;
		}

		const arr = filters[key as keyof BaseStatFilter] as unknown[] | undefined;
		if (arr && arr.length > 0) {
			arr.forEach((val) => params.append(key, String(val)));
		}
	}
}

/**
 * Append entity-specific extra params to URLSearchParams.
 * Handles string, number, boolean, and string/number array values.
 */
function appendExtraParams(
	params: URLSearchParams,
	extra: Record<string, unknown> | undefined
): void {
	if (!extra) return;

	for (const [key, val] of Object.entries(extra)) {
		if (val === undefined || val === null) continue;

		if (Array.isArray(val)) {
			val.forEach((v) => params.append(key, String(v)));
		} else {
			params.append(key, String(val));
		}
	}
}
